
import React, { JSX, useCallback, useEffect, useState } from 'react'
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { AssetSummary } from 'types/api'
import listMyAssetsQuery from 'graphql/listMyAssets.graphql'

const formatBytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

export const assetDisplayName = (asset: AssetSummary): string => {
  if (asset.fileName !== null && asset.fileName !== '') return asset.fileName
  try {
    return new URL(asset.publicUrl).pathname.split('/').pop() ?? asset.assetId
  } catch {
    return asset.assetId
  }
}

export const isReplacedAsset = (asset: AssetSummary): boolean =>
  typeof asset.replacedByAssetId === 'string' && asset.replacedByAssetId !== ''

export const assetNameMatches = (asset: AssetSummary, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (q === '') return true
  return assetDisplayName(asset).toLowerCase().includes(q)
}

export const AssetListPager = ({
  page,
  pageCount,
  shownFrom,
  shownTo,
  total,
  onPage
}: {
  page: number
  pageCount: number
  shownFrom: number
  shownTo: number
  total: number
  onPage: (page: number) => void
}): JSX.Element | null => {
  if (pageCount <= 1) return null
  const buttonClass = 'rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
  return (
    <div className='mt-3 flex items-center justify-between gap-2'>
      <span className='text-[11px] tabular-nums text-dm-text-accent'>
        Showing {shownFrom}-{shownTo} of {total}
      </span>
      <div className='flex items-center gap-1.5'>
        <button
          type='button'
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          className={buttonClass}
        >
          Prev
        </button>
        <span className='text-[11px] tabular-nums text-dm-text-accent'>
          {page} / {pageCount}
        </span>
        <button
          type='button'
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount}
          className={buttonClass}
        >
          Next
        </button>
      </div>
    </div>
  )
}

const PICKER_PAGE_SIZE = 24

const pickableAssets = (assets: AssetSummary[]): AssetSummary[] => assets

export const AssetPickerModal = ({
  onClose,
  onSelect
}: {
  onClose: () => void
  onSelect: (publicUrl: string) => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [assets, setAssets] = useState<AssetSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const loadAssets = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setError(null)
    try {
      const { assets: fetched } = await graphqlRequest<{ assets: AssetSummary[] }>(
        listMyAssetsQuery, {}, auth0Token
      )
      setAssets(fetched)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your uploads')
      setAssets([])
    }
  }, [auth0Token])

  useEffect(() => {
    void loadAssets()
  }, [loadAssets])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onClose])

  const pickable = assets === null ? null : pickableAssets(assets)
  const matching = pickable === null ? null : pickable.filter((a) => assetNameMatches(a, query))
  const pageCount = matching === null ? 1 : Math.max(1, Math.ceil(matching.length / PICKER_PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const pageItems = matching === null
    ? []
    : matching.slice((currentPage - 1) * PICKER_PAGE_SIZE, currentPage * PICKER_PAGE_SIZE)

  let body: JSX.Element
  if (assets === null || pickable === null || matching === null) {
    body = (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinner />
      </div>
    )
  } else if (assets.length === 0) {
    body = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <PhotoIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>No uploads yet. Images you upload anywhere in the app land in your library.</p>
      </div>
    )
  } else if (matching.length === 0) {
    body = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
        <PhotoIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p data-testid='asset-picker-no-matches'>No images match that name.</p>
      </div>
    )
  } else {
    body = (
      <ul
        className='grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4'
        data-testid='asset-picker-grid'
      >
        {pageItems.map((asset) => (
          <li key={asset.assetId}>
            <button
              type='button'
              onClick={() => onSelect(asset.publicUrl)}
              title={assetDisplayName(asset)}
              className='group w-full overflow-hidden rounded-lg border border-white/5 bg-dm-bg-accent/30 text-left transition-colors hover:border-brand/50'
            >
              <div className='aspect-square w-full bg-dm-bg-accent/50'>
                <img
                  src={asset.publicUrl}
                  alt={assetDisplayName(asset)}
                  loading='lazy'
                  className='size-full object-cover'
                  onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
                />
              </div>
              <div className='flex flex-col gap-0.5 px-2.5 py-2 leading-tight'>
                <span className='truncate text-[11px] font-medium text-dm-text-primary'>
                  {assetDisplayName(asset)}
                </span>
                <span className='truncate text-[10px] tabular-nums text-dm-text-accent'>
                  {formatBytes(asset.sizeBytes)}
                  {isReplacedAsset(asset) && (
                    <span className='ml-1.5 rounded bg-white/10 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-dm-text-secondary'>Replaced</span>
                  )}
                </span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div
      data-testid='asset-picker-modal'
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
    >
      <div
        className='absolute inset-0 bg-black/70'
        onClick={onClose}
        aria-hidden='true'
      />
      <div className='relative w-full max-w-2xl rounded-xl border border-white/10 bg-dm-bg-secondary p-5 shadow-xl'>
        <div className='mb-4 flex items-center justify-between gap-3'>
          <h3 className='text-base font-semibold text-dm-text-primary'>Select from uploads</h3>
          <button
            type='button'
            onClick={onClose}
            title='Close'
            className='rounded-md p-1.5 text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
          >
            <XMarkIcon className='size-4' aria-hidden='true' />
            <span className='sr-only'>Close</span>
          </button>
        </div>
        {error !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
          </div>
        )}

        {pickable !== null && pickable.length > 0 && (
          <input
            type='search'
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1) }}
            placeholder='Search by name'
            aria-label='Search images by name'
            data-testid='asset-picker-search'
            className='mb-3 w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
          />
        )}
        {body}
        {matching !== null && matching.length > 0 && (
          <AssetListPager
            page={currentPage}
            pageCount={pageCount}
            shownFrom={(currentPage - 1) * PICKER_PAGE_SIZE + 1}
            shownTo={(currentPage - 1) * PICKER_PAGE_SIZE + pageItems.length}
            total={matching.length}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  )
}
